import { createSlug } from './urlHelpers';
import { createResourceLocatorString, pathByRouteName } from './routes';

export const POST_AUTH_INTENT_STORAGE_KEY = 'postAuthIntent';
export const POST_AUTH_INTENT_MAX_AGE_MS = 30 * 60 * 1000;

export const POST_AUTH_ACTIONS = {
  BROWSE: 'browse',
  BOOK: 'book',
  CHECKOUT: 'checkout',
  CART: 'cart',
  INQUIRY: 'inquiry',
};

const AUTH_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/confirm',
  '/recover-password',
  '/reset-password',
  '/verify-email',
  '/customer/reset-password',
  '/provider/reset-password',
  '/customer/verify-email',
  '/provider/verify-email',
];

const BOOKING_ACTIONS = [
  POST_AUTH_ACTIONS.BOOK,
  POST_AUTH_ACTIONS.CHECKOUT,
  POST_AUTH_ACTIONS.CART,
];

export const isValidReturnPath = returnPath => {
  if (!returnPath || typeof returnPath !== 'string') {
    return false;
  }
  if (!returnPath.startsWith('/') || returnPath.startsWith('//')) {
    return false;
  }
  const pathname = parseReturnPath(returnPath).pathname;
  return !AUTH_PATH_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

export const parseReturnPath = returnPath => {
  try {
    const url = new URL(returnPath, 'http://local');
    return {
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
    };
  } catch (e) {
    return { pathname: '/', search: '', hash: '' };
  }
};

export const inferActionFromReturnPath = returnPath => {
  if (!returnPath) {
    return POST_AUTH_ACTIONS.BROWSE;
  }
  const { pathname } = parseReturnPath(returnPath);
  if (pathname.includes('/checkout')) {
    return POST_AUTH_ACTIONS.CHECKOUT;
  }
  if (pathname === '/cart') {
    return POST_AUTH_ACTIONS.CART;
  }
  if (pathname.startsWith('/l/')) {
    return POST_AUTH_ACTIONS.BOOK;
  }
  return POST_AUTH_ACTIONS.BROWSE;
};

export const getPostAuthIntent = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(POST_AUTH_INTENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const intent = JSON.parse(raw);
    if (!intent?.createdAt || Date.now() - intent.createdAt > POST_AUTH_INTENT_MAX_AGE_MS) {
      clearPostAuthIntent();
      return null;
    }
    return intent;
  } catch (e) {
    return null;
  }
};

export const savePostAuthIntent = intent => {
  if (typeof window === 'undefined' || !intent?.returnPath) {
    return;
  }
  if (!isValidReturnPath(intent.returnPath)) {
    return;
  }
  const payload = {
    ...intent,
    createdAt: intent.createdAt || Date.now(),
  };
  window.sessionStorage.setItem(POST_AUTH_INTENT_STORAGE_KEY, JSON.stringify(payload));
};

export const clearPostAuthIntent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(POST_AUTH_INTENT_STORAGE_KEY);
};

export const hasActiveBookingIntent = (intent = getPostAuthIntent()) =>
  !!intent && BOOKING_ACTIONS.includes(intent.action);

export const mergeFromIntoIntent = (fromPath, action) => {
  if (!isValidReturnPath(fromPath)) {
    return;
  }
  const existing = getPostAuthIntent();
  const resolvedAction =
    action || existing?.action || inferActionFromReturnPath(fromPath);

  savePostAuthIntent({
    ...existing,
    returnPath: fromPath,
    action: resolvedAction,
    createdAt: Date.now(),
  });
};

export const savePostAuthIntentForLocation = (location, action) => {
  if (!location) {
    return;
  }
  const returnPath = `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
  mergeFromIntoIntent(returnPath, action);
};

export const redirectToAuthPage = ({
  history,
  routes,
  location,
  authRouteName = 'LoginPage',
  action,
  listingId,
}) => {
  const returnPath = `${location.pathname}${location.search}${location.hash}`;
  const resolvedAction = action || inferActionFromReturnPath(returnPath);

  savePostAuthIntent({
    returnPath,
    action: resolvedAction,
    listingId,
    createdAt: Date.now(),
  });

  history.push(createResourceLocatorString(authRouteName, routes, {}, {}), { from: returnPath });
};

export const getCheckoutDataFromStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem('checkoutData');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const buildCheckoutRedirectTarget = (routeConfiguration, { bookingLink = true } = {}) => {
  const checkoutData = getCheckoutDataFromStorage();
  const listing = checkoutData?.listing;
  const listingId = listing?.id?.uuid;
  if (!listingId || !routeConfiguration) {
    return null;
  }

  const authorName = listing?.author?.attributes?.profile?.displayName;
  const slug = createSlug(authorName || 'listing');
  const pathname = pathByRouteName('CheckoutPage', routeConfiguration, { id: listingId, slug });
  const search = bookingLink ? '?bookingLink=true' : '';

  return {
    type: 'checkout',
    pathname,
    search,
    hash: '',
    setupCheckout: true,
  };
};

export const getResolvedUserType = (user, fallbackUserType = 'customer') =>
  user?.attributes?.profile?.publicData?.userType || fallbackUserType;

/**
 * Returns a redirect target if signup/profile onboarding is incomplete, otherwise null.
 */
export const getOnboardingRedirectTarget = (user, userType) => {
  const resolvedUserType = getResolvedUserType(user, userType);
  const publicData = user?.attributes?.profile?.publicData || {};
  const protectedData = user?.attributes?.profile?.protectedData || {};
  const emailVerified = user?.attributes?.emailVerified;
  const { isPhoneNumberVerified, profileType, isDealListingPublished } = publicData;
  const { phoneNumber } = protectedData || {};

  if (resolvedUserType === 'provider') {
    if (!emailVerified && !isDealListingPublished) {
      return {
        type: 'named',
        name: 'MerchantDetailsPage',
        params: { screen: 'update-profile' },
        clearIntent: false,
      };
    }
    if (!isPhoneNumberVerified) {
      return {
        type: 'named',
        name: 'MerchantDetailsPage',
        params: { screen: phoneNumber ? 'verify-phone' : 'update-profile' },
        clearIntent: false,
      };
    }
    if (!profileType) {
      return {
        type: 'named',
        name: 'MerchantDetailsPage',
        params: { screen: 'choose-profile' },
        clearIntent: false,
      };
    }
    return null;
  }

  if (!isPhoneNumberVerified) {
    return {
      type: 'named',
      name: 'CustomerDetailsPage',
      params: { screen: phoneNumber ? 'verify-phone' : 'update-profile' },
      clearIntent: false,
    };
  }

  return null;
};

export const isOnboardingComplete = (user, userType) =>
  !getOnboardingRedirectTarget(user, userType);

/**
 * Resolve where to send the user after login/signup.
 * Onboarding first; saved return path only after onboarding is complete.
 */
export const resolvePostAuthRedirect = ({
  user,
  userType,
  isBookingLink = false,
  routeConfiguration,
}) => {
  const intent = getPostAuthIntent();
  const resolvedUserType = getResolvedUserType(user, userType);

  const onboardingTarget = getOnboardingRedirectTarget(user, userType);
  if (onboardingTarget) {
    return onboardingTarget;
  }

  const wantsCheckout =
    isBookingLink ||
    intent?.action === POST_AUTH_ACTIONS.CHECKOUT ||
    (intent?.returnPath && intent.returnPath.includes('/checkout'));

  if (wantsCheckout && routeConfiguration) {
    const checkoutTarget = buildCheckoutRedirectTarget(routeConfiguration, { bookingLink: true });
    if (checkoutTarget) {
      return { ...checkoutTarget, clearIntent: true };
    }
  }

  if (intent?.returnPath && isValidReturnPath(intent.returnPath)) {
    return {
      type: 'path',
      ...parseReturnPath(intent.returnPath),
      clearIntent: true,
    };
  }

  if (resolvedUserType === 'provider') {
    return {
      type: 'named',
      name: 'LandingPage',
      clearIntent: true,
    };
  }

  return {
    type: 'named',
    name: 'LandingPage',
    clearIntent: true,
  };
};

export const redirectToSavedReturnPath = (history, { fallback = '/' } = {}) => {
  const returnPath = consumePostAuthReturnPath();
  history.push(returnPath || fallback);
};

export const consumePostAuthReturnPath = () => {
  const intent = getPostAuthIntent();
  if (intent?.returnPath && isValidReturnPath(intent.returnPath)) {
    clearPostAuthIntent();
    return intent.returnPath;
  }
  return null;
};
